/**
 * Public university page (/universities/<university> by default): header
 * with the university's stats (no portal link — owner, 2026-09-29), industry chips, a title
 * search and a TRL filter over its technologies (24 per page, titles only),
 * each card linking to the technology's page.
 *
 * The filters live in the query string (?q=&industry=&trl=&page=, page
 * 1-based) so a filtered view can be linked and the back button works; the
 * resolver turns them into by-path list parameters. The canonical URL (in
 * page.seo) is always the unfiltered one.
 */

import React, { useEffect, useRef, useState } from 'react';
import { COUNTRY_ISO, flagSrc } from '../NewHome';
import type { ListParams, PageState, UniversityPageData } from './api';
import { useTpShell } from './PublicShell';
import { usePageSeo } from './seo';
import { Breadcrumbs, PreviewSwitch, SearchIcon } from './shared';
import { tpClip, tpTrack, useScrollDepth, useSectionViews } from './track';

interface Props {
  page: UniversityPageData;
  busy: boolean;
  list: ListParams;
  onList: (next: ListParams) => void;
  onPreview: (s: PageState) => void;
}

const hardGo = (href: string) => { window.location.href = href; };

/** 1 … 4 5 [6] 7 8 … 40 (0-based page numbers; null = a gap). */
const pagerItems = (page: number, pages: number): Array<number | null> => {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i);
  const out: Array<number | null> = [0];
  const lo = Math.max(1, page - 2);
  const hi = Math.min(pages - 2, page + 2);
  if (lo > 1) out.push(null);
  for (let i = lo; i <= hi; i++) out.push(i);
  if (hi < pages - 2) out.push(null);
  out.push(pages - 1);
  return out;
};

export const UniversityPage: React.FC<Props> = ({ page, busy, list, onList, onPreview }) => {
  const shell = useTpShell();
  const code = page.code;
  const techs = page.techs || { rows: [], total: 0, page: 0, pages: 0, per_page: 24 };
  const gridRef = useRef<HTMLDivElement>(null);

  usePageSeo(page.seo);

  const viewed = useRef('');
  useEffect(() => {
    if (viewed.current === code) return;
    viewed.current = code;
    tpTrack('unipage_view', { code, state: page.state });
  }, [code, page.state]);
  useSectionViews('unipageSection', 'unipage_section_view', code, section => ({ section, code }));
  useScrollDepth('unipage_scroll_depth', code, { code });

  // ---- search box: debounced into the URL ----
  const [q, setQ] = useState(list.q || '');
  const listQ = list.q || '';
  useEffect(() => { setQ(listQ); }, [listQ]);
  const listRef = useRef(list);
  listRef.current = list;
  useEffect(() => {
    const clean = q.trim().slice(0, 100);
    if (clean === (listRef.current.q || '')) return;
    const t = window.setTimeout(() => {
      tpTrack('unipage_search', { q_len: clean.length, code });
      onList({ ...listRef.current, q: clean || undefined, page: 0 });
    }, 450);
    return () => window.clearTimeout(t);
    // onList is stable enough for a debounce; the query text drives it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, code]);

  const toGrid = () => {
    try {
      const el = gridRef.current;
      if (el && el.getBoundingClientRect().top < 0) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch { /* ignore */ }
  };

  const setIndustry = (name: string | null) => {
    tpTrack('unipage_filter', { filter: 'industry', value: tpClip(name || 'all', 120), code });
    onList({ ...list, industry: name || undefined, page: 0 });
  };
  const setTrl = (v: string) => {
    tpTrack('unipage_filter', { filter: 'trl', value: v || 'any', code });
    onList({ ...list, trl: v ? Number(v) : undefined, page: 0 });
  };
  const goPage = (n: number) => {
    tpTrack('unipage_page', { page: n, code });
    onList({ ...list, page: n });
    toGrid();
  };
  const clearAll = () => {
    tpTrack('unipage_filter', { filter: 'clear', value: 'all', code });
    setQ('');
    onList({});
  };

  const industries = (page.stats?.industries || []).slice(0, 12);
  const filtered = !!(list.q || list.industry || list.trl);
  const first = techs.total ? techs.page * techs.per_page + 1 : 0;
  const last = Math.min(techs.total, techs.page * techs.per_page + techs.rows.length);

  return (
    <div className="tpg-wrap tpg-page">
      {page.admin_preview?.allowed && (
        <PreviewSwitch states={page.admin_preview.states || []} current={page.state} busy={busy} onPick={onPreview} />
      )}

      {page.ui?.breadcrumbs && (
        <Breadcrumbs
          items={page.breadcrumbs || []}
          onClick={(level, path, e) => { tpTrack('unipage_breadcrumb_click', { level, code }); shell.go(path, e); }}
        />
      )}

      <header className="tpg-uni-head" data-unipage-section="header">
        <div>
          <div className="tpg-eyebrow">University</div>
          <h1 className="tpg-h1">{page.name}</h1>
          <p className="tpg-subline" style={{ marginBottom: 0 }}>
            {page.label && page.label !== page.name && <span>{page.label}</span>}
            {page.country && (
              <span>
                {COUNTRY_ISO[page.country] && <img className="tpg-flag" src={flagSrc(page.country)} alt="" width={18} height={13} loading="lazy" />}
                {page.country}
              </span>
            )}
            {page.tto_name && <span>Technology transfer: {page.tto_name}</span>}
          </p>
          <ul className="tpg-stats">
            <li><strong>{(page.stats?.technologies || 0).toLocaleString('en-US')}</strong><span>technologies</span></li>
            {typeof page.stats?.researchers === 'number' && page.stats.researchers > 0 && (
              <li><strong>{page.stats.researchers.toLocaleString('en-US')}</strong><span>researchers</span></li>
            )}
            {industries.length > 0 && <li><strong>{industries.length}</strong><span>industries</span></li>}
          </ul>
        </div>
        <div className="tpg-actions">
          <button
            type="button"
            className="tpg-btn-primary"
            onClick={() => {
              // Signed in: straight to Cosmos. Signed out: log in first, then
              // land in Cosmos (Google/LinkedIn sign-in ends there anyway).
              const signedIn = shell.signedIn || page.state !== 'pre_login';
              tpTrack('unipage_cta_click', { cta: 'explore_cosmos', code, signed_in: signedIn });
              if (signedIn) hardGo('/');
              else shell.openLogin('unipage_explore_cosmos', '/');
            }}
          >Explore in Cosmos</button>
        </div>
      </header>

      <hr className="tpg-divider" />

      {industries.length > 0 && (
        <div className="tpg-chips" style={{ marginBottom: 16 }} data-unipage-section="industries" role="group" aria-label="Filter by industry">
          <button type="button" className="tpg-chip-btn" aria-pressed={!list.industry} onClick={() => setIndustry(null)}>All industries</button>
          {industries.map(ind => (
            <button
              key={ind.name}
              type="button"
              className="tpg-chip-btn"
              aria-pressed={list.industry === ind.name}
              onClick={() => setIndustry(list.industry === ind.name ? null : ind.name)}
            >{ind.name} <span>{ind.n.toLocaleString('en-US')}</span></button>
          ))}
        </div>
      )}

      <div className="tpg-toolbar">
        <form className="tpg-search" role="search" onSubmit={e => {
          e.preventDefault();
          const clean = q.trim().slice(0, 100);
          if (clean === (list.q || '')) return;
          tpTrack('unipage_search', { q_len: clean.length, code });
          onList({ ...list, q: clean || undefined, page: 0 });
        }}>
          <SearchIcon />
          <label htmlFor="tpg-uni-q" className="tpg-hp">Search this university&apos;s technologies</label>
          <input
            id="tpg-uni-q"
            className="tpg-input"
            type="search"
            value={q}
            maxLength={100}
            placeholder={`Search ${page.label || page.name} technologies`}
            onChange={e => setQ(e.target.value)}
          />
        </form>
        <label htmlFor="tpg-uni-trl" className="tpg-hp">Technology readiness level</label>
        <select id="tpg-uni-trl" className="tpg-input" style={{ width: 'auto' }} value={list.trl ? String(list.trl) : ''} onChange={e => setTrl(e.target.value)}>
          <option value="">Any TRL</option>
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(n => <option key={n} value={String(n)}>TRL {n}</option>)}
        </select>
        {filtered && <button type="button" className="tpg-linkbtn" onClick={clearAll}>Clear filters</button>}
      </div>

      <div ref={gridRef} data-unipage-section="techs" className={busy ? 'tpg-busy' : undefined} aria-busy={busy || undefined} style={{ scrollMarginTop: 96 }}>
        <p className="tpg-count" aria-live="polite">
          {techs.total
            ? <>Showing {first.toLocaleString('en-US')}–{last.toLocaleString('en-US')} of {techs.total.toLocaleString('en-US')} technologies{filtered ? ' matching your filters' : ''}</>
            : filtered ? 'No technologies match these filters.' : 'No technologies are listed for this university yet.'}
        </p>
        {techs.rows.length > 0 ? (
          <div className="tpg-cards">
            {techs.rows.map((r, i) => (
              <a
                key={r.id}
                className="tpg-tcard"
                href={r.path}
                onClick={e => {
                  tpTrack('unipage_tech_click', { target_id: r.id, rank: techs.page * techs.per_page + i + 1, page: techs.page, code });
                  shell.go(r.path, e);
                }}
              >
                <span className="tpg-tcard-title">{r.title}</span>
                <span className="tpg-tcard-meta">
                  {typeof r.trl_level === 'number' && r.trl_level > 0 && <span className="tpg-badge">TRL {r.trl_level}</span>}
                  {r.industry && <span>{r.industry}</span>}
                </span>
              </a>
            ))}
          </div>
        ) : filtered ? (
          <button type="button" className="tpg-btn-secondary" onClick={clearAll}>Clear filters</button>
        ) : null}

        {techs.pages > 1 && (
          <nav className="tpg-pager" aria-label="Pages" data-unipage-section="pager">
            <button type="button" disabled={techs.page <= 0} onClick={() => goPage(techs.page - 1)} aria-label="Previous page">‹</button>
            {pagerItems(techs.page, techs.pages).map((n, i) => n === null
              ? <span key={`gap${i}`} className="tpg-gap" aria-hidden="true">…</span>
              : (
                <button
                  key={n}
                  type="button"
                  aria-current={n === techs.page ? 'page' : undefined}
                  onClick={() => { if (n !== techs.page) goPage(n); }}
                >{n + 1}</button>
              ))}
            <button type="button" disabled={techs.page >= techs.pages - 1} onClick={() => goPage(techs.page + 1)} aria-label="Next page">›</button>
          </nav>
        )}
      </div>
    </div>
  );
};

export default UniversityPage;
